import { FFC_OPEX_BREAKOUT } from '../model/scenarios';
import {
  cloneDefaultMarketingPhases,
  normalizeMarketingPhases,
} from '../model/marketingPhases';
import { normalizePlanMixGrowth } from '../model/funnel';

export default function AssumptionsPanel({ assumptions, onChange }) {
  const update = (patch) => onChange({ ...assumptions, ...patch });

  const updateArpu = (tier, value) => {
    update({
      arpuByTier: {
        ...assumptions.arpuByTier,
        [tier]: Math.max(0, parseFloat(value) || 0),
      },
    });
  };

  const updateMix = (tier, value) => {
    update({
      planMix: {
        ...assumptions.planMix,
        [tier]: Math.min(1, Math.max(0, parseFloat(value) || 0)),
      },
    });
  };

  const phases = normalizeMarketingPhases(
    Array.isArray(assumptions.marketingPhases)
      ? assumptions.marketingPhases
      : cloneDefaultMarketingPhases()
  );

  const updatePhase = (index, patch) => {
    const next = phases.map((p, i) => (i === index ? { ...p, ...patch } : p));
    update({
      marketingPhases: next,
      marketingBudgetMo: next[0]?.budgetMo ?? assumptions.marketingBudgetMo ?? 0,
    });
  };

  const addPhase = () => {
    const last = phases[phases.length - 1];
    const previous = phases[phases.length - 2];
    const throughMonth =
      last?.throughMonth >= 60
        ? Math.min(
            59,
            Math.max((previous?.throughMonth || 1) + 1, last.throughMonth - 12)
          )
        : Math.min(60, (last?.throughMonth || 12) + 12);
    const next = [
      ...phases,
      {
        throughMonth,
        budgetMo: last?.budgetMo || 200,
        label: 'New phase',
      },
    ];
    update({ marketingPhases: next });
  };

  const removePhase = (index) => {
    if (phases.length <= 1) return;
    const next = phases.filter((_, i) => i !== index);
    update({
      marketingPhases: next,
      marketingBudgetMo: next[0]?.budgetMo ?? 0,
    });
  };

  const mixTotal = Object.values(assumptions.planMix || {}).reduce((s, v) => s + (v || 0), 0);
  const mixGrowth = normalizePlanMixGrowth(assumptions.planMixGrowth);
  const fixed = assumptions.fixedOpexMo || 0;
  const marketing = phases[0]?.budgetMo ?? assumptions.marketingBudgetMo ?? 0;
  const envelope = fixed + marketing;

  return (
    <section className="chart-card economics-panel">
      <h2>Core assumptions</h2>
      <p className="chart-hint economics-muted">
        Cash envelope (infra + phased marketing). Marketing cash ≠ organic growth — Growth tab /
        Channels organic stay independent. Default: marketing also funds paid acquisition (÷CAC).
        Paid channels: Google / Reddit / LinkedIn — <strong>not Meta Ads</strong> (organic FB/IG
        posts only). Plaid, Stripe, and AI are variable COGS/logo.
      </p>

      <div className="economics-form-grid economics-form-grid-3">
        <label className="economics-field">
          <span>Opening MRR ($)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={assumptions.openingMrr}
            onChange={(e) => update({ openingMrr: Math.max(0, parseFloat(e.target.value) || 0) })}
          />
        </label>
        <label className="economics-field">
          <span>Opening logos (paying customers)</span>
          <input
            type="number"
            min="0"
            step="1"
            value={assumptions.openingLogos}
            onChange={(e) => update({ openingLogos: Math.max(0, parseFloat(e.target.value) || 0) })}
          />
        </label>
        <label className="economics-field">
          <span>Horizon (months)</span>
          <input
            type="number"
            min="1"
            max="60"
            value={assumptions.horizonMonths}
            onChange={(e) =>
              update({
                horizonMonths: Math.min(60, Math.max(1, parseInt(e.target.value, 10) || 12)),
              })
            }
          />
        </label>
        <label className="economics-field">
          <span>Starting cash ($)</span>
          <input
            type="number"
            min="0"
            step="1000"
            value={assumptions.startingCash}
            onChange={(e) => update({ startingCash: Math.max(0, parseFloat(e.target.value) || 0) })}
          />
        </label>
        <label className="economics-field">
          <span>Fixed opex / mo ($)</span>
          <input
            type="number"
            min="0"
            step="10"
            value={assumptions.fixedOpexMo}
            onChange={(e) => update({ fixedOpexMo: Math.max(0, parseFloat(e.target.value) || 0) })}
          />
          <span className="economics-muted">Infra + tools only</span>
        </label>
        <label className="economics-field">
          <span>Plaid COGS / linked logo ($)</span>
          <input
            type="number"
            min="0"
            step="0.1"
            value={assumptions.plaidCogsPerLinkedLogo ?? 0}
            onChange={(e) =>
              update({ plaidCogsPerLinkedLogo: Math.max(0, parseFloat(e.target.value) || 0) })
            }
          />
          <span className="economics-muted">
            Charged only to paid logos that complete bank linking
          </span>
        </label>
        <label className="economics-field">
          <span>Stripe COGS / paid logo ($)</span>
          <input
            type="number"
            min="0"
            step="0.1"
            value={assumptions.stripeCogsPerPaidLogo ?? 0}
            onChange={(e) =>
              update({ stripeCogsPerPaidLogo: Math.max(0, parseFloat(e.target.value) || 0) })
            }
          />
          <span className="economics-muted">Charged to every paid logo with subscription revenue</span>
        </label>
        <label className="economics-field">
          <span>AI COGS / logo ($)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={assumptions.aiCogsPerLogo ?? 0}
            onChange={(e) =>
              update({ aiCogsPerLogo: Math.max(0, parseFloat(e.target.value) || 0) })
            }
          />
          <span className="economics-muted">Blended vendor cost (Premium+; 0 Free/Basic)</span>
        </label>
        <label className="economics-field">
          <span>AI pack rev / logo ($)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={assumptions.aiPackRevenuePerLogo ?? 0}
            onChange={(e) =>
              update({ aiPackRevenuePerLogo: Math.max(0, parseFloat(e.target.value) || 0) })
            }
          />
          <span className="economics-muted">Overage packs only (not in MRR)</span>
        </label>
        <label className="economics-field">
          <span>Expansion % / mo</span>
          <input
            type="number"
            min="0"
            max="20"
            step="0.1"
            value={assumptions.expansionPctMo}
            onChange={(e) =>
              update({ expansionPctMo: Math.min(20, Math.max(0, parseFloat(e.target.value) || 0)) })
            }
          />
        </label>
        <label className="economics-field">
          <span>Contraction % / mo</span>
          <input
            type="number"
            min="0"
            max="20"
            step="0.1"
            value={assumptions.contractionPctMo}
            onChange={(e) =>
              update({
                contractionPctMo: Math.min(20, Math.max(0, parseFloat(e.target.value) || 0)),
              })
            }
          />
        </label>
      </div>

      <h3 className="economics-subheading">Marketing phases (cash schedule)</h3>
      <p className="chart-hint economics-muted">
        Learning → Proven → Scale. Raises spend only after CAC/payback clears. Linked paid uses the
        active month&apos;s phase budget ÷ CAC. No Meta Ads.
      </p>
      {phases.length === 0 && (
        <p className="module-status">
          No phased marketing spend. Paid acquisition is off until you add a phase or enable a
          custom paid budget in Channels.
        </p>
      )}
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Through month (after Now)</th>
              <th>$/mo</th>
              <th>Label</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {phases.map((phase, index) => (
              <tr key={`phase-${index}`}>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    min="1"
                    max="60"
                    value={phase.throughMonth ?? 12}
                    onChange={(e) =>
                      updatePhase(index, {
                        throughMonth: Math.min(
                          60,
                          Math.max(1, parseInt(e.target.value, 10) || 1)
                        ),
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input"
                    type="number"
                    min="0"
                    step="10"
                    value={phase.budgetMo ?? 0}
                    onChange={(e) =>
                      updatePhase(index, {
                        budgetMo: Math.max(0, parseFloat(e.target.value) || 0),
                      })
                    }
                  />
                </td>
                <td>
                  <input
                    className="economics-inline-input economics-inline-input-wide"
                    value={phase.label || ''}
                    onChange={(e) => updatePhase(index, { label: e.target.value })}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => removePhase(index)}
                    disabled={phases.length <= 1}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="secondary-button" onClick={addPhase}>
        Add phase
      </button>

      <p className="chart-hint">
        Early envelope: ${fixed.toFixed(0)} infra + ${Number(marketing).toFixed(0)} learning
        marketing = <strong>${envelope.toFixed(0)}/mo</strong> (before variable COGS)
      </p>

      <h3 className="economics-subheading">Infra / tools breakout (illustrative)</h3>
      <p className="chart-hint economics-muted">
        Edit fixed opex to match your bill. Default sum = $
        {FFC_OPEX_BREAKOUT.reduce((s, r) => s + r.amount, 0)}.
      </p>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>$/mo</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {FFC_OPEX_BREAKOUT.map((row) => (
              <tr key={row.item}>
                <td>{row.item}</td>
                <td>${row.amount}</td>
                <td className="economics-muted">Fixed opex</td>
              </tr>
            ))}
            <tr>
              <td>Plaid / Stripe</td>
              <td>—</td>
              <td className="economics-muted">Bank COGS / logo</td>
            </tr>
            <tr>
              <td>AI chat (Premium+)</td>
              <td>—</td>
              <td className="economics-muted">AI COGS / logo + pack rev / logo</td>
            </tr>
            <tr>
              <td>Paid acquisition (Google / Reddit / LinkedIn)</td>
              <td>phased</td>
              <td className="economics-muted">Not Meta Ads · organic FB/IG OK</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 className="economics-subheading">ARPU by tier</h3>
      <div className="economics-form-grid">
        {['basic', 'premium', 'enterprise'].map((tier) => (
          <label key={tier} className="economics-field">
            <span>{tier}</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={assumptions.arpuByTier?.[tier] ?? 0}
              onChange={(e) => updateArpu(tier, e.target.value)}
            />
          </label>
        ))}
      </div>

      <h3 className="economics-subheading">Plan mix (shares)</h3>
      <div className="economics-form-grid">
        {['basic', 'premium', 'enterprise'].map((tier) => (
          <label key={tier} className="economics-field">
            <span>{tier}</span>
            <input
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={assumptions.planMix?.[tier] ?? 0}
              onChange={(e) => updateMix(tier, e.target.value)}
            />
          </label>
        ))}
      </div>
      <p className="chart-hint">
        Mix total: {(mixTotal * 100).toFixed(0)}%
        {Math.abs(mixTotal - 1) > 0.05 && ' — normalize to ~100% for accurate ARPU'}
        . Opening mix is month 0. After that, upgrade rates below move share
        Basic→Premium and Premium→Enterprise (Enterprise stays 0 unless that rate
        is &gt; 0).
      </p>

      <h3 className="economics-subheading">Plan mix evolution (% of logos / mo)</h3>
      <div className="economics-form-grid">
        <label className="economics-field">
          <span>Basic → Premium</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={mixGrowth.upgradeBasicToPremiumPctMo}
            onChange={(e) =>
              update({
                planMixGrowth: {
                  ...mixGrowth,
                  upgradeBasicToPremiumPctMo: Math.min(
                    100,
                    Math.max(0, parseFloat(e.target.value) || 0)
                  ),
                },
              })
            }
          />
        </label>
        <label className="economics-field">
          <span>Premium → Enterprise</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={mixGrowth.upgradePremiumToEnterprisePctMo}
            onChange={(e) =>
              update({
                planMixGrowth: {
                  ...mixGrowth,
                  upgradePremiumToEnterprisePctMo: Math.min(
                    100,
                    Math.max(0, parseFloat(e.target.value) || 0)
                  ),
                },
              })
            }
          />
        </label>
      </div>
      <p className="chart-hint economics-muted">
        Default 0.5% Basic→Premium, 0% into Enterprise. Families / operators with
        companies belong on Premium (multi-book), not Enterprise. Set Premium→Enterprise
        only if you want that SKU to appear from growth.
      </p>
    </section>
  );
}
